import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  ClipboardList, 
  Users, 
  Receipt, 
  Settings, 
  LogOut, 
  Menu, 
  X,
  RefreshCw,
  CheckSquare,
  Bell
} from 'lucide-react';
import { Button } from './ui/button';

const Layout = ({ children }) => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/suivi', label: 'Suivi', icon: ClipboardList },
    { path: '/clients', label: 'Clients', icon: Users },
    { path: '/mise-a-jour', label: 'Mise à jour', icon: RefreshCw },
    { path: '/checklist-bilan', label: 'Checklist Bilan', icon: CheckSquare },
    { path: '/facturation', label: 'Facturation', icon: Receipt },
    { path: '/rappels', label: 'Rappels Clients', icon: Bell },
  ];

  if (isAdmin()) {
    navItems.push({ path: '/parametres', label: 'Paramètres', icon: Settings });
  }

  const NavItem = ({ item }) => (
    <NavLink
      to={item.path}
      onClick={() => setSidebarOpen(false)}
      className={({ isActive }) =>
        `pill-nav-item flex items-center gap-3 w-full ${isActive ? 'active' : ''}`
      }
      data-testid={`nav-${item.path.slice(1)}`}
    >
      <item.icon size={18} />
      <span>{item.label}</span>
    </NavLink>
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Mobile menu button */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="lg:hidden fixed top-4 left-4 z-[60] p-2 bg-white rounded-lg border border-slate-200"
        data-testid="mobile-menu-button"
      >
        {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/20 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`sidebar-container ${sidebarOpen ? 'open' : ''} hidden lg:flex`}
        data-testid="sidebar"
      >
        {/* Logo/Title */}
        <div className="p-6 border-b border-slate-200">
          <h1 className="text-xl font-bold text-slate-900 tracking-tight">
            Performance<br/>Transmission
          </h1>
          <p className="text-sm text-slate-500 mt-3 pb-1" data-testid="welcome-message">
            Salut {user?.first_name} 🙂
          </p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2">
          {navItems.map((item) => (
            <NavItem key={item.path} item={item} />
          ))}
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-slate-200">
          <div className="mb-3 px-4">
            <p className="text-sm font-medium text-slate-900">
              {user?.first_name} {user?.last_name}
            </p>
            <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
          </div>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start gap-3 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg"
            data-testid="logout-button"
          >
            <LogOut size={18} />
            <span>Déconnexion</span>
          </Button>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      <aside 
        className={`sidebar-container lg:hidden ${sidebarOpen ? 'open' : ''}`}
        style={{ display: sidebarOpen ? 'flex' : 'none' }}
      >
        <div className="p-6 border-b border-slate-200">
          <h1 className="text-xl font-bold text-slate-900 tracking-tight">
            Performance<br/>Transmission
          </h1>
          <p className="text-sm text-slate-500 mt-3 pb-1">
            Salut {user?.first_name} 🙂
          </p>
        </div>
        <nav className="flex-1 p-4 space-y-2">
          {navItems.map((item) => (
            <NavItem key={item.path} item={item} />
          ))}
        </nav>
        <div className="p-4 border-t border-slate-200">
          <div className="mb-3 px-4">
            <p className="text-sm font-medium text-slate-900">
              {user?.first_name} {user?.last_name}
            </p>
            <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
          </div>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start gap-3 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg"
          >
            <LogOut size={18} />
            <span>Déconnexion</span>
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <main className="main-content">
        <div className="lg:hidden h-12" /> {/* Spacer for mobile menu button */}
        {children}
      </main>
    </div>
  );
};

export default Layout;
